import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "./Icon";
import PhoneMock from "./PhoneMock";
import { easing, lerp, tween } from "../lib/tween";

/**
 * The hero on the landing and services pages, built as a scene the visitor can
 * push around.
 *
 * A radial gradient the whole section sits inside, a phone held at the centre
 * that turns towards the pointer, a field of glass orbs drifting around it
 * that the pointer shoves out of the way, and bubbles rising through the lot.
 * Picking a world re-lights the gradient, spins the phone through two turns
 * behind a motion blur and swaps its screen at the peak, while the orbs
 * collapse to the centre, change what they carry and scatter back out.
 *
 * Everything moves from one requestAnimationFrame loop and one pointer
 * listener. Nine orbs each running their own listener and their own frame is
 * the usual way a hero like this ends up dropping frames on a laptop.
 *
 * The scene owns the motion and nothing else: the copy, the calls to action
 * and whatever closes the band all arrive as props, so the two pages share a
 * skeleton without sharing a word of content.
 */

/* -------------------------------------------------------------------- orbs */

/**
 * Where each orb sits before anything has moved it, as a percentage of the
 * stage. `layer` decides whether it passes in front of the copy or behind the
 * phone — the pair of depths is what stops the field reading as one flat plane
 * of stickers.
 */
const ORB_SLOTS = [
  { top: "24%", left: "29%", size: 128, layer: "fg", opacity: 1 },
  { top: "61%", left: "41%", size: 66, layer: "fg", opacity: 1 },
  { top: "29%", left: "63%", size: 140, layer: "fg", opacity: 1 },
  { top: "14%", left: "49%", size: 86, layer: "fg", opacity: 1 },
  { top: "74%", left: "19%", size: 74, layer: "fg", opacity: 1 },
  { top: "45%", left: "76%", size: 108, layer: "fg", opacity: 1 },
  { top: "16%", left: "39%", size: 56, layer: "bg", opacity: 0.7 },
  { top: "51%", left: "55%", size: 50, layer: "bg", opacity: 0.6 },
  { top: "79%", left: "34%", size: 54, layer: "bg", opacity: 0.7 }
];

/** Seconds per bob, deliberately co-prime-ish so the field never pulses as one. */
const FLOAT_DURATIONS = [5, 7, 6, 8, 5.5, 6.5, 9, 11, 10];

const REPEL_RADIUS = 400;
const REPEL_STRENGTH = 80;
const POINTER_EASE = 0.05;
const REPEL_EASE = 0.1;

/* ------------------------------------------------------------------ timing */

const SPIN_IN_MS = 600;
const SPIN_OUT_MS = 1500;
const IMPLODE_MS = 500;
const HOLD_MS = 300;
const EXPLODE_MS = 900;
const MAX_BLUR = 15;

const BUBBLE_EVERY_MS = 400;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* --------------------------------------------------------------- component */

function HeroStage({
  worlds,
  eyebrow,
  title,
  lede,
  actions,
  footnote,
  sideTitle,
  /**
   * Which card renders as selected. Defaults to whichever world is on stage;
   * pass `null` to show none — the services page needs that for "All", where
   * the stage is still showing a world but no single group is filtered.
   */
  highlightKey,
  onWorldChange,
  children
}) {
  const [active, setActive] = useState(0);
  const [switching, setSwitching] = useState(false);

  const stageRef = useRef(null);
  const phoneRef = useRef(null);
  const glowRef = useRef(null);
  const layerRefs = useRef({ fg: null, bg: null });
  const bubbleRef = useRef(null);

  /** Per-orb physics. Index matches ORB_SLOTS. */
  const orbs = useRef(
    ORB_SLOTS.map((slot) => ({
      el: null,
      glyph: null,
      rx: 0,
      ry: 0,
      baseX: 0,
      baseY: 0,
      angle: Math.random() * 360,
      opacity: slot.opacity
    }))
  );

  const pointer = useRef({ x: 0, y: 0, px: -9999, py: -9999 });
  const smoothed = useRef({ x: 0, y: 0 });

  /** Orbs are driven by the switch choreography rather than the frame loop. */
  const orbsBusy = useRef(false);
  /** Guards against a second world being picked mid-transition. */
  const busy = useRef(false);
  const activeIndex = useRef(0);
  /** Everything that has to be torn down if the hero unmounts mid-transition. */
  const running = useRef([]);

  const world = worlds[active];
  const highlighted = highlightKey === undefined ? world.key : highlightKey;

  /**
   * Places an orb the first time its node appears, and never again. Switching
   * worlds re-renders the field to change the glyphs, and seeding the node a
   * second time would slam it back to its starting position and opacity —
   * mid-transition that reads as the whole field flashing back into view for a
   * frame before the scatter picks it up.
   */
  const registerOrb = useCallback((index, el) => {
    const orb = orbs.current[index];
    if (!el || orb.el === el) return;
    orb.el = el;
    orb.glyph = el.firstElementChild;
    el.style.opacity = String(orb.opacity);
    el.style.transform = `translate3d(${orb.baseX}px, ${orb.baseY}px, 0) rotate(${orb.angle}deg)`;
  }, []);

  /* ------------------------------------------------------ pointer + frames */

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    const onMove = (event) => {
      pointer.current.x = event.clientX / window.innerWidth - 0.5;
      pointer.current.y = event.clientY / window.innerHeight - 0.5;
      pointer.current.px = event.clientX;
      pointer.current.py = event.clientY;
    };

    window.addEventListener("mousemove", onMove, { passive: true });

    let frame = requestAnimationFrame(function step(now) {
      const time = now * 0.001;
      const cursor = pointer.current;
      const eased = smoothed.current;

      eased.x += (cursor.x - eased.x) * POINTER_EASE;
      eased.y += (cursor.y - eased.y) * POINTER_EASE;

      // The phone leans towards the pointer. The spin the world switch adds
      // lives in its own property, so a turn in progress never wipes out the
      // tracking and vice versa.
      const phone = phoneRef.current;
      if (phone) {
        phone.style.setProperty("--mw-ry", `${eased.x * 26}deg`);
        phone.style.setProperty("--mw-rx", `${eased.y * -15}deg`);
      }

      // Three parallax rates: the orbs in front travel with the pointer, the
      // ones behind against it, and the light behind everything barely moves.
      if (layerRefs.current.fg) {
        layerRefs.current.fg.style.transform = `translate3d(${eased.x * 60}px, ${eased.y * 60}px, 0)`;
      }
      if (layerRefs.current.bg) {
        layerRefs.current.bg.style.transform = `translate3d(${eased.x * -30}px, ${eased.y * -30}px, 0)`;
      }
      if (glowRef.current) {
        glowRef.current.style.transform = `translate3d(${eased.x * -15}px, ${eased.y * -15}px, 0)`;
      }

      if (!orbsBusy.current) {
        orbs.current.forEach((orb, i) => {
          const el = orb.el;
          // offsetParent is null while the layer is display:none, which is how
          // the field is dropped on small screens. Nothing to place.
          if (!el || !el.offsetParent) return;

          const rect = el.getBoundingClientRect();
          const dx = cursor.px - (rect.left + rect.width / 2);
          const dy = cursor.py - (rect.top + rect.height / 2);
          const distance = Math.sqrt(dx * dx + dy * dy) || 1;

          let targetX = 0;
          let targetY = 0;
          let speed = 1;

          if (distance < REPEL_RADIUS) {
            const force = (REPEL_RADIUS - distance) / REPEL_RADIUS;
            targetX = (dx / distance) * force * -REPEL_STRENGTH;
            targetY = (dy / distance) * force * -REPEL_STRENGTH;
            // Shoved orbs also spin harder, so the push has some weight to it.
            speed = 1 + force * 5;
          }

          orb.rx += (targetX - orb.rx) * REPEL_EASE;
          orb.ry += (targetY - orb.ry) * REPEL_EASE;
          orb.angle += 0.2 * speed;

          const phase = (time + i * 0.7) * ((Math.PI * 2) / FLOAT_DURATIONS[i % 9]);
          const floatY = Math.sin(phase) * 15;
          const floatAngle = Math.cos(phase) * 6;
          const angle = orb.angle + floatAngle;

          el.style.transform = `translate3d(${orb.baseX + orb.rx}px, ${
            orb.baseY + orb.ry + floatY
          }px, 0) rotate(${angle}deg)`;
          // The disc turns so its highlight moves; the glyph is turned back so
          // it stays readable.
          if (orb.glyph) orb.glyph.style.transform = `rotate(${-angle}deg)`;
        });
      }

      frame = requestAnimationFrame(step);
    });

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(frame);
    };
  }, []);

  /* ---------------------------------------------------------------- bubbles */

  useEffect(() => {
    const host = bubbleRef.current;
    if (!host || prefersReducedMotion()) return undefined;

    const timers = new Set();

    const spawn = () => {
      if (document.hidden) return;

      const bubble = document.createElement("span");
      const size = Math.random() * 20 + 10;
      const duration = Math.random() * 6 + 4;

      bubble.className = "mw-bubble";
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;
      bubble.style.left = `${Math.random() * 100}%`;
      bubble.style.setProperty("--mw-bubble-op", (Math.random() * 0.4 + 0.2).toFixed(2));
      bubble.style.animationDuration = `${duration}s`;

      host.appendChild(bubble);

      const timer = setTimeout(() => {
        timers.delete(timer);
        bubble.remove();
      }, duration * 1000);
      timers.add(timer);
    };

    const interval = setInterval(spawn, BUBBLE_EVERY_MS);

    return () => {
      clearInterval(interval);
      timers.forEach(clearTimeout);
      host.replaceChildren();
    };
  }, []);

  useEffect(
    () => () => {
      running.current.forEach((stop) => stop());
      running.current = [];
    },
    []
  );

  /* ----------------------------------------------------------------- switch */

  const applyTheme = useCallback((theme) => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.style.setProperty("--hero-inner", theme.inner);
    stage.style.setProperty("--hero-mid", theme.mid);
    stage.style.setProperty("--hero-outer", theme.outer);
  }, []);

  const switchWorld = useCallback(
    (index) => {
      if (busy.current) return;

      const next = worlds[index];

      // Told even when the stage is already showing this world: on the
      // services page the same card can be clicked again to come back to that
      // group after the toolbar has been set to All.
      if (onWorldChange) onWorldChange(next, index);
      if (index === activeIndex.current) return;

      // The gradient starts moving immediately and keeps morphing underneath
      // the spin, so the scene has already changed colour by the time the
      // phone comes back round.
      applyTheme(next.theme);

      if (prefersReducedMotion()) {
        activeIndex.current = index;
        setActive(index);
        return;
      }

      busy.current = true;
      orbsBusy.current = true;
      setSwitching(true);

      // `busy` means nothing from the previous switch is still running, so the
      // list can be emptied rather than growing a set of dead cancels per
      // switch for the life of the page.
      running.current = [];
      const track = (stop) => {
        running.current.push(stop);
        return stop;
      };

      /* --- the phone: two turns, blurred through the middle --------------- */

      const phone = phoneRef.current;
      const spin = (value) => phone && phone.style.setProperty("--mw-spin", `${value}deg`);
      const blur = (value) => {
        if (phone) phone.style.filter = value > 0.05 ? `blur(${value}px)` : "none";
      };

      track(
        tween({
          duration: SPIN_IN_MS,
          ease: easing.power2In,
          onUpdate: (p) => {
            spin(p * 360);
            blur(p * MAX_BLUR);
          },
          onComplete: () => {
            // Swapped at the peak: the phone is a blurred edge-on sliver and
            // every orb is fully transparent, so nothing is seen changing.
            activeIndex.current = index;
            setActive(index);

            track(
              tween({
                duration: SPIN_OUT_MS,
                ease: easing.backOut(0.7),
                onUpdate: (p) => {
                  spin(360 + p * 360);
                  // backOut overshoots past 1, and a negative blur radius
                  // invalidates the whole filter.
                  blur(Math.max(0, MAX_BLUR - p * MAX_BLUR));
                },
                onComplete: () => {
                  spin(0);
                  blur(0);
                  busy.current = false;
                  setSwitching(false);
                }
              })
            );
          }
        })
      );

      /* --- the orbs: collapse, change, scatter ---------------------------- */

      const plans = orbs.current.map((orb) => {
        const el = orb.el;
        const layer = el && el.offsetParent;

        return {
          orb,
          fromX: orb.baseX + orb.rx,
          fromY: orb.baseY + orb.ry,
          fromAngle: orb.angle,
          // Every orb converges on the middle of its own layer, which is where
          // the phone is — measured off the untransformed box so a half-played
          // repulsion can't drag the target with it.
          centerX: layer ? layer.clientWidth / 2 - el.offsetLeft - el.offsetWidth / 2 : 0,
          centerY: layer ? layer.clientHeight / 2 - el.offsetTop - el.offsetHeight / 2 : 0,
          toX: (Math.random() - 0.5) * 200,
          toY: (Math.random() - 0.5) * 200
        };
      });

      const place = (plan, x, y, angle, scale, opacity) => {
        const { el, glyph, opacity: base } = plan.orb;
        if (!el) return;
        el.style.opacity = String(base * opacity);
        el.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${angle}deg) scale(${scale})`;
        if (glyph) glyph.style.transform = `rotate(${-angle}deg)`;
      };

      track(
        tween({
          duration: IMPLODE_MS,
          ease: easing.power2In,
          onUpdate: (p) => {
            plans.forEach((plan) =>
              place(
                plan,
                lerp(plan.fromX, plan.centerX, p),
                lerp(plan.fromY, plan.centerY, p),
                plan.fromAngle + 45 * p,
                lerp(1, 0.1, p),
                1 - p
              )
            );
          },
          onComplete: () => {
            const hold = setTimeout(() => {
              track(
                tween({
                  duration: EXPLODE_MS,
                  ease: easing.backOut(1.5),
                  onUpdate: (p) => {
                    plans.forEach((plan) =>
                      place(
                        plan,
                        lerp(plan.centerX, plan.toX, p),
                        lerp(plan.centerY, plan.toY, p),
                        plan.fromAngle + 45 + 45 * p,
                        lerp(0.1, 1, p),
                        Math.min(1, p * 2)
                      )
                    );
                  },
                  onComplete: () => {
                    plans.forEach((plan) => {
                      const { orb } = plan;
                      orb.baseX = plan.toX;
                      orb.baseY = plan.toY;
                      orb.angle = plan.fromAngle + 90;
                      orb.rx = 0;
                      orb.ry = 0;
                      if (orb.el) orb.el.style.opacity = String(orb.opacity);
                    });
                    // Handing the field back to the frame loop, which picks the
                    // orbs up from the positions just committed.
                    orbsBusy.current = false;
                  }
                })
              );
            }, HOLD_MS);

            track(() => clearTimeout(hold));
          }
        })
      );
    },
    [applyTheme, onWorldChange, worlds]
  );

  const step = useCallback(
    (direction) => {
      const next = (activeIndex.current + direction + worlds.length) % worlds.length;
      switchWorld(next);
    },
    [switchWorld, worlds.length]
  );

  /* ------------------------------------------------------------------ view */

  const orbsFor = (layer) =>
    ORB_SLOTS.map((slot, i) =>
      slot.layer === layer ? (
        <span
          key={slot.layer + i}
          ref={(el) => registerOrb(i, el)}
          className="mw-orb"
          style={{
            top: slot.top,
            left: slot.left,
            width: `${slot.size}px`,
            height: `${slot.size}px`
          }}
          aria-hidden="true"
        >
          {/* The glyph is its own element because the frame loop turns it
              back against the disc — Icon renders a bare <svg> and takes no
              style prop, so the size lives on this wrapper. */}
          <span
            className="block"
            style={{ width: slot.size * 0.4, height: slot.size * 0.4 }}
          >
            <Icon name={world.orbIcons[i]} className="h-full w-full" />
          </span>
        </span>
      ) : null
    );

  return (
    <section ref={stageRef} className="mw-stage relative isolate overflow-hidden">
      {/* ----------------------------- lighting ----------------------------- */}
      <div ref={glowRef} className="absolute inset-0" aria-hidden="true">
        <div className="mw-aurora absolute -left-32 -top-40 h-[36rem] w-[36rem] rounded-full bg-[#7C3AED]/30 blur-[120px]" />
        <div
          className="mw-aurora absolute -bottom-56 right-[-8rem] h-[40rem] w-[40rem] rounded-full bg-[#A855F7]/22 blur-[140px]"
          style={{ animationDelay: "-9s" }}
        />
        <div
          className="mw-aurora absolute left-1/2 top-1/4 h-[28rem] w-[28rem] rounded-full bg-[#C084FC]/14 blur-[130px]"
          style={{ animationDelay: "-17s" }}
        />
      </div>

      <div
        className="mw-beam absolute -top-1/4 left-[12%] h-[150%] w-[18rem]"
        style={{ "--mw-beam-rot": "14deg" }}
        aria-hidden="true"
      />
      <div
        className="mw-beam absolute -top-1/4 right-[8%] h-[150%] w-[13rem]"
        style={{ "--mw-beam-rot": "-11deg", animationDelay: "-11s" }}
        aria-hidden="true"
      />
      <div className="mw-grid-dark absolute inset-0" aria-hidden="true" />
      <div className="mw-sheen absolute inset-0" aria-hidden="true" />
      <div
        className="mw-grain absolute inset-0 opacity-[0.13] mix-blend-overlay"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_88%_72%_at_50%_44%,transparent_44%,rgba(9,1,15,0.66)_100%)]"
        aria-hidden="true"
      />

      <div ref={bubbleRef} className="absolute inset-0 overflow-hidden" aria-hidden="true" />

      {/* ------------------------------ content ----------------------------- */}
      <div className="relative mx-auto grid min-h-screen w-full max-w-7xl grid-cols-1 items-stretch gap-y-10 px-6 pb-16 pt-28 md:px-10 lg:grid-cols-12 lg:gap-x-8 lg:pb-20 lg:pt-32">
        {/* --------------------------- orbs, behind ---------------------------
            Both orb layers hang off this box rather than off the section, so
            "the middle of the layer" is the middle of the scene — where the
            phone is — and not the middle of the section, which is dragged
            down by whatever the page hangs below the fold. */}
        <div
          ref={(el) => {
            layerRefs.current.bg = el;
          }}
          className="pointer-events-none absolute inset-0 z-0 hidden lg:block"
          aria-hidden="true"
        >
          {orbsFor("bg")}
        </div>

        {/* --- left --- */}
        <div className="z-20 flex flex-col lg:col-span-5">
          {eyebrow}

          <h1 className="mw-script mt-8 text-white [text-shadow:0_18px_45px_rgba(9,1,15,0.55)]">
            {title}
          </h1>

          <p className="mw-lede mt-7 max-w-[30rem] text-[#DCC9F2]">{lede}</p>

          <div className="mt-9 flex flex-wrap items-center gap-3">{actions}</div>

          {/* Pinned to the bottom of the column, the way the scene's other
              corner marks are. */}
          {footnote ? <div className="mt-12 lg:mt-auto">{footnote}</div> : null}
        </div>

        {/* --- the phone ---------------------------------------------------
            In the flow on small screens, lifted out of it from lg up so it
            can sit dead centre with the copy running past either side. */}
        <div className="z-10 flex items-center justify-center lg:pointer-events-none lg:absolute lg:inset-0 lg:col-span-2">
          {/* Scale, float and perspective each get their own element: the
              float animates `transform`, so anything sharing that element
              would be overwritten every frame. */}
          <div className="scale-[0.62] sm:scale-75 lg:scale-[0.82] xl:scale-95">
            <div className="mw-phone-float">
              <div className="mw-phone-scene">
                <div ref={phoneRef} className="mw-phone">
                  <PhoneMock world={world} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- right --- */}
        <div className="z-20 flex flex-col items-center gap-10 text-center lg:col-span-5 lg:items-end lg:justify-between lg:text-right">
          <div className="flex flex-col items-center gap-5 lg:items-end">
            <div className="flex flex-wrap justify-center gap-3 lg:justify-end">
              {worlds.map((item, i) => {
                const isActive = item.key === highlighted;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => switchWorld(i)}
                    aria-pressed={isActive}
                    disabled={switching}
                    className={`mw-world-card group relative w-[8.25rem] rounded-[1.75rem] border p-4 pt-14 text-center backdrop-blur-md focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FBCFE8]/40 disabled:cursor-progress ${
                      isActive
                        ? "border-[#FBCFE8] bg-white/[0.09]"
                        : "border-white/12 bg-white/[0.05] hover:border-white/30 hover:bg-white/[0.11]"
                    }`}
                  >
                    <span
                      className="mw-world-glyph absolute left-1/2 top-0 flex h-[4.5rem] w-[4.5rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl text-white shadow-[0_22px_38px_-16px_rgba(0,0,0,0.9)] ring-1 ring-white/25"
                      style={{ background: item.accent.promo }}
                    >
                      <Icon name={item.glyph} className="h-7 w-7" />
                    </span>
                    <span className="block text-[0.8rem] font-bold text-white">
                      {item.label}
                    </span>
                    <span className="mt-1 block text-[0.68rem] leading-snug text-white/60">
                      {item.tagline}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => step(-1)}
                disabled={switching}
                aria-label="Previous world"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors duration-300 hover:bg-white/15 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/25 disabled:opacity-40"
              >
                <Icon name="arrowLeft" className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                disabled={switching}
                aria-label="Next world"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors duration-300 hover:bg-white/15 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/25 disabled:opacity-40"
              >
                <Icon name="arrow" className="h-4 w-4" />
              </button>
            </div>
          </div>

          <h2 className="mw-script text-white [text-shadow:0_18px_45px_rgba(9,1,15,0.55)]">
            {sideTitle}
          </h2>
        </div>

        {/* --------------------------- orbs, in front -------------------------
            Above the copy, which is what sells them as being in the room
            rather than printed on the background. They never take the pointer,
            so the buttons underneath still work. */}
        <div
          ref={(el) => {
            layerRefs.current.fg = el;
          }}
          className="pointer-events-none absolute inset-0 z-30 hidden lg:block"
          aria-hidden="true"
        >
          {orbsFor("fg")}
        </div>
      </div>

      {/* Whatever the page wants to close the band with — a stats rail, in the
          same gradient rather than a second dark section under it. */}
      {children ? <div className="relative z-20">{children}</div> : null}

      {/* Live region so the switch is announced to a screen reader, which sees
          none of the choreography. */}
      <p className="sr-only" aria-live="polite">
        {world.label} — {world.tagline}
      </p>
    </section>
  );
}

export default HeroStage;
